/**
 * `gantt` — monday's `TimelineGanttBoardView` (`specs/M4-V3-gantt.md`).
 *
 * All eight production rows store the **same nine keys** and nothing else, and the Timeline view
 * (V5) stores that identical set — monday's two kinds are one component with different chrome
 * `[cap:views/INDEX.md]`. So this is the settings type for both, and `views/timeline/` is a
 * second registry entry over it rather than a copy.
 */
import { z } from 'zod';
import { defineViewType, normaliseOverDefaults } from '../types.js';
import { mondayShapedSettings } from '../from-monday.js';
import { columnSelectionSchema, EMPTY_COLUMN_SELECTION, isColumnSelected, } from '../column-selection.js';
import {} from '../timeline-axis.js';
// The kind's own monday reader, so `normaliseSettings` can recognise an imported blob
// (P-025). Re-exported below as well, which is how every caller outside has always had it.
import { ganttSettingsFromMonday } from './from-monday.js';
export * from './from-monday.js';
export * from './to-monday.js';
export const TIMELINE_PSEUDO_GROUP_TOKENS = new Set(['group', 'groups']);
/** `true` when the token means "the board's own groups" rather than a column. */
export function isBoardGroupToken(token) {
    return token !== null && TIMELINE_PSEUDO_GROUP_TOKENS.has(token);
}
/**
 * `null` and `'none'` are the same thing to the renderer — one ungrouped bucket, which is what
 * monday draws for `{"group_by_id":{"none":true}}` and for a row that omits the key entirely.
 * They stay distinct in the settings only so the round trip can tell "monday stored none" from
 * "monday stored nothing" (`[V3§4]`).
 */
export function isUngroupedToken(token) {
    return token === null || token === 'none';
}
/** The `settings_str` keys this kind models. Everything else survives in `mondayExtras`. */
export const GANTT_MONDAY_KEYS = new Set([
    'columns',
    'group_by_id',
    'color_by_id',
    'label_by_id',
    'show_today_line',
    'show_weekends',
    'show_rollup',
    'enable_visual_dependencies',
    'display_legend',
]);
export const TIMELINE_ZOOM_SCHEMA = z.enum(['days', 'weeks', 'months', 'quarters', 'years']);
export const ganttViewSettingsSchema = z.object({
    dateColumns: columnSelectionSchema,
    groupBy: z.string().nullable(),
    colorBy: z.string().nullable(),
    labelBy: z.string().nullable(),
    showTodayLine: z.boolean(),
    showWeekends: z.boolean(),
    showRollup: z.boolean(),
    showDependencies: z.boolean(),
    displayLegend: z.boolean(),
    zoom: TIMELINE_ZOOM_SCHEMA,
    collapsedGroupKeys: z.array(z.string()),
    mondayKeys: z.array(z.string()),
    mondayExtras: z.record(z.string(), z.unknown()),
});
/**
 * What monday's own "add a Gantt view" produced on the sandbox: no column chosen, grouped by
 * nothing, every visual on, opening at Quarters `[cap sandbox 280337853]`. The five booleans
 * default **on** because half the production rows omit them and still render them on (q424).
 */
export const DEFAULT_GANTT_VIEW_SETTINGS = {
    dateColumns: EMPTY_COLUMN_SELECTION,
    // `null`, not `'none'`: a view whose blob carries no `group_by_id` must not gain one on the
    // way back out, and `isUngroupedToken` makes the two read the same.
    groupBy: null,
    colorBy: null,
    labelBy: null,
    showTodayLine: true,
    showWeekends: true,
    showRollup: true,
    showDependencies: true,
    displayLegend: true,
    /**
     * **P-033.** Measured again on the account's own Gantt (`174316936` / `1756489`,
     * `discovery/ui-reference/parity/174316936/view-1756489-gantt-monday.png`): monday opens it
     * on the current week — `W37 7 – 13` highlighted, a today line through it — at **weeks**.
     * The old `quarters` came from `prod-gantt`, which was a *stored* zoom of a view somebody
     * had zoomed out, not the kind's opening scale; the zoom is per-user and never written to
     * monday (q423), so this default is what a board opens at and nothing else.
     */
    zoom: 'weeks',
    collapsedGroupKeys: [],
    mondayKeys: [],
    mondayExtras: {},
};
/** Measured on the production Gantt (`[V3§2.1]`); one place, so no component re-measures. */
export const GANTT_METRICS = {
    sidePanelWidth: 416,
    headerMajorHeight: 33,
    headerMinorHeight: 32,
    groupHeaderHeight: 48,
    rowHeight: 36,
    barHeight: 20,
    barRadius: 4,
    /** (36 − 20) / 2 — the clearance above and below a bar inside its row. */
    barInset: 8,
    handleWidth: 10,
    handleGripWidth: 2,
    handleGripHeight: 14,
    /** Where the bar's text label starts, measured from the right handle's right edge. */
    labelGap: 6,
    rollupHeight: 26,
    rollupRadius: 8,
    rollupProgressHeight: 4,
    milestoneSize: 23,
    legendHeight: 53,
    legendDotSize: 12,
    weekendFill: '#f6f7fb',
    handleFill: '#676879',
};
/**
 * Which column a bar comes from (`[V3§4.1]`): the first **selected** column in board order whose
 * cell is non-empty, preferring a `timeline` over a `date`. Every one of the 14 real Gantt and
 * Timeline rows names exactly one timeline column among its selections, so the preference is
 * what makes `{"all":true,"load_deadline7":true,"timeline0":true}` draw a span and not a dot.
 */
export function barColumnFor(input) {
    const selected = input.columns.filter((column) => isColumnSelected(input.settings.dateColumns, column.id));
    // An unconfigured Gantt (`columns: {}`) places by every date-ish column, exactly as an
    // unconfigured calendar does (`[V4§4]`) — otherwise a fresh view is permanently blank.
    const candidates = selected.length > 0 ? selected : input.columns;
    const nonEmpty = candidates.filter((column) => {
        const value = input.values[column.id];
        return value !== null && value !== undefined && typeof value === 'object';
    });
    return (nonEmpty.find((column) => column.type === 'timeline')?.id ??
        nonEmpty[0]?.id ??
        null);
}
export const ganttView = defineViewType({
    id: 'gantt',
    title: 'Gantt',
    mondayViewType: 'TimelineGanttBoardView',
    kind: 'APP',
    settingsSchema: ganttViewSettingsSchema,
    defaultSettings: DEFAULT_GANTT_VIEW_SETTINGS,
    normaliseSettings: (input) => normaliseOverDefaults('gantt', ganttViewSettingsSchema, DEFAULT_GANTT_VIEW_SETTINGS, mondayShapedSettings(input, DEFAULT_GANTT_VIEW_SETTINGS, ganttSettingsFromMonday)),
    requiredColumnTypes: [],
    supportsFilters: true,
    supportsSort: true,
    supportsGroupBy: true,
    canBeDefault: true,
    implemented: true,
    plannedIn: null,
    gallery: { order: 2, section: 'board-views' },
});
//# sourceMappingURL=index.js.map